import { useState } from 'react';
import { Check } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useSubscription } from '@/hooks/useSubscription';
import { PLANS } from '@/lib/constants';
import {
  getPlanOption,
  getYearlySavingsPercent,
  type BillingCycle,
  type CheckoutPlan,
  checkoutPlanFromVariant,
} from '@/lib/pricing';
import { PlanChangeConfirmDialog } from '@/components/paywall/PlanChangeConfirmDialog';

interface PlanPickerCardsProps {
  cycle: BillingCycle;
  onCycleChange: (cycle: BillingCycle) => void;
  compact?: boolean;
  className?: string;
}

function checkoutPlanFor(planType: string, cycle: 'monthly' | 'yearly' | null | undefined): CheckoutPlan | null {
  const c = cycle === 'yearly' ? 'yearly' : 'monthly';
  if (planType === 'premium') return c === 'yearly' ? 'yearly' : 'monthly';
  if (planType === 'family') return c === 'yearly' ? 'family-yearly' : 'family-monthly';
  return null;
}

export function PlanPickerCards({
  cycle,
  onCycleChange,
  compact = false,
  className,
}: PlanPickerCardsProps) {
  const { planType, isPremium, isTrialActive, lsSubscriptionId, lsVariantId, refetch } = useSubscription();
  const [pendingPlan, setPendingPlan] = useState<CheckoutPlan | null>(null);

  const premium = getPlanOption('premium', cycle);
  const family = getPlanOption('family', cycle);
  const yearlySave = getYearlySavingsPercent('premium');

  const hasLiveSub = !!lsSubscriptionId && isPremium && !isTrialActive;
  const currentCheckoutPlan = checkoutPlanFromVariant(lsVariantId);
  const currentPlanType: 'premium' | 'family' | null =
    hasLiveSub && (planType === 'premium' || planType === 'family') ? planType : null;

  const pad = compact ? 'p-5' : 'p-6';
  const priceSize = compact ? 'text-2xl' : 'text-3xl';
  const bulletText = compact ? 'text-xs' : 'text-sm';
  const bullets = compact ? PLANS.premium.valueBullets.slice(0, 4) : PLANS.premium.valueBullets;
  const familyBullets = compact ? PLANS.family.valueBullets.slice(0, 3) : PLANS.family.valueBullets;

  const handleClick = (plan: CheckoutPlan) => {
    setPendingPlan(plan);
  };

  const renderCta = (planKey: 'premium' | 'family', plan: CheckoutPlan, defaultLabel: string, variant: 'default' | 'outline') => {
    const isCurrentVariant = currentCheckoutPlan === plan;
    const isCurrentPlanType = currentPlanType === planKey;
    let label = defaultLabel;
    if (hasLiveSub) {
      if (isCurrentVariant) label = 'Current plan';
      else if (isCurrentPlanType) label = `Switch to ${plan.includes('yearly') ? 'Yearly' : 'Monthly'}`;
      else label = `Switch to ${planKey === 'premium' ? 'Individual' : 'Family'}`;
    }
    return (
      <Button
        className="w-full"
        variant={variant}
        onClick={() => handleClick(plan)}
        disabled={isCurrentVariant}
      >
        {label}
      </Button>
    );
  };

  return (
    <div className={cn('w-full', className)}>
      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-2 mb-5">
        <button
          type="button"
          onClick={() => onCycleChange('monthly')}
          className={cn(
            'px-4 py-2 rounded-xl text-sm font-medium transition-colors',
            cycle === 'monthly'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          Monthly
        </button>
        <button
          type="button"
          onClick={() => onCycleChange('yearly')}
          className={cn(
            'px-4 py-2 rounded-xl text-sm font-medium transition-colors inline-flex items-center gap-1.5',
            cycle === 'yearly'
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          )}
        >
          Yearly
          {yearlySave > 0 && (
            <span
              className={cn(
                'text-[9px] px-1.5 py-0.5 rounded',
                cycle === 'yearly'
                  ? 'bg-primary-foreground/15 text-primary-foreground'
                  : 'bg-primary/15 text-primary'
              )}
            >
              Save {yearlySave}%
            </span>
          )}
        </button>
      </div>

      {/* Cards */}
      <div className="grid sm:grid-cols-2 gap-4">
        {/* Individual */}
        <Card className="relative overflow-hidden border-primary/40">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
          <CardContent className={cn(pad, 'relative')}>
            <p className="text-[10px] text-primary/70 font-medium mb-1">
              Most people choose this
            </p>
            <h3 className="text-lg font-semibold">{premium.name}</h3>
            <p className="text-xs text-muted-foreground/50 mb-3">
              For people serious about control
            </p>

            <div className="flex items-baseline gap-1">
              <span className={cn(priceSize, 'font-bold tracking-tight')}>
                {premium.headlinePrice}
              </span>
              <span className="text-muted-foreground text-sm">{premium.period}</span>
            </div>
            <p className="text-[10px] text-muted-foreground/50 mb-4">
              {premium.subtext}
            </p>

            {renderCta('premium', premium.checkoutPlan, 'Start Individual', 'default')}
            <p className="text-[10px] text-muted-foreground/40 text-center mt-1">
              {hasLiveSub ? 'Prorated. No double-charge.' : 'Cancel anytime'}
            </p>

            <ul className="space-y-2 mt-5">
              {bullets.map((b) => (
                <li key={b} className={cn('flex items-start gap-2 text-muted-foreground', bulletText)}>
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  {b}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        {/* Family */}
        <Card className="relative overflow-hidden">
          <CardContent className={cn(pad, 'relative')}>
            <h3 className="text-lg font-semibold">{family.name}</h3>
            <p className="text-xs text-muted-foreground/50 mb-3">
              For doing this together
            </p>

            <div className="flex items-baseline gap-1">
              <span className={cn(priceSize, 'font-bold tracking-tight')}>
                {family.headlinePrice}
              </span>
              <span className="text-muted-foreground text-sm">{family.period}</span>
            </div>
            <p className="text-[10px] text-muted-foreground/50 mb-4">
              {family.subtext}
            </p>

            {renderCta('family', family.checkoutPlan, 'Build together', 'outline')}
            <p className="text-[10px] text-muted-foreground/40 text-center mt-1">
              Invite up to {family.includedUsers} people
            </p>

            <ul className="space-y-2 mt-5">
              {familyBullets.map((b) => (
                <li key={b} className={cn('flex items-start gap-2 text-muted-foreground', bulletText)}>
                  <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                  {b}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      </div>

      <PlanChangeConfirmDialog
        open={!!pendingPlan}
        plan={pendingPlan}
        onOpenChange={(o) => { if (!o) setPendingPlan(null); }}
        onConfirmed={refetch}
      />
    </div>
  );
}
